import { CloseButtonSvg } from '@/components/SvgComponents/CloseButtonSvg'
import { dialogState } from '@/features/dialogSlice'
import { clearBodyScrollLock, useClearBodyScrollLock } from '@/lib/clearBodyScrollLock'
import type { RootState } from '@/store/store'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useRouter } from 'next/router'
import type React from 'react'
import { memo, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

type Props = {
  children: React.ReactNode
  title?: string
  titleSmall?: boolean
  description?: string
  image?: string
  buttonOff?: boolean
  selectorState?: boolean
  dispatchAction?: any
  closeFunc?: () => void
  ignoreDisableBodyLock?: boolean
  reloadOnClose?: boolean
}

const Dialog = memo(
  ({
    children,
    title,
    titleSmall = false,
    description,
    image,
    buttonOff,
    selectorState,
    dispatchAction,
    closeFunc,
    ignoreDisableBodyLock,
    reloadOnClose,
  }: Props) => {
    const dialogStateSelector = dispatchAction
      ? selectorState
      : useSelector((state: RootState) => state.dialog.dialogState)

    const router = useRouter()
    const dispatch = useDispatch()
    const dialogElem = useRef(null)

    useEffect(() => {
      // ignoreDisableBodyLock パラメーターがtrueの場合かつ、ダイアログが表示された場合
      // スクロールを無効化しない
      if (ignoreDisableBodyLock && dialogStateSelector === true) {
        return
      }

      if (dialogStateSelector) {
        disableBodyScroll(dialogElem.current)
        return
      }

      enableBodyScroll(dialogElem.current)
      clearBodyScrollLock()
      return
    }, [ignoreDisableBodyLock, dialogStateSelector])

    useClearBodyScrollLock()

    const CloseButton = () => {
      closeFunc && closeFunc()

      if (dispatchAction) {
        dispatch(dispatchAction(false))
      } else {
        dispatch(dialogState(false))
      }
      if (reloadOnClose) {
        router.reload()
      }
    }

    return (
      <article className="c-dialog" id="my-dialog" aria-hidden={!dialogStateSelector}>
        <div className="c-dialog-overlay" onClick={() => CloseButton()} />
        <div className="c-dialog-wrap">
          {buttonOff ? (
            ''
          ) : (
            <button type="button" className="c-dialog-close" onClick={() => CloseButton()}>
              <CloseButtonSvg />
            </button>
          )}
          <div
            role="dialog"
            className="c-dialog-content"
            aria-labelledby="dialog"
            ref={dialogElem}
            key={`dialog_${Date.now()}`}
          >
            {title && (
              <h1
                className={`text-gray-600 text-center ${titleSmall ? 'text-h2' : 'text-h1'} font-bold`}
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              />
            )}

            {image && (
              <div className="mx-auto mt-5 h-24 w-24 rounded-full text-center">
                <img src={`${process.env.NEXT_PUBLIC_SITEURL}/images/img_demo_stamp.png`} alt={image} />
              </div>
            )}

            {description && (
              <p
                className="text-gray-600 mt-5 text-center text-sm font-bold"
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
              />
            )}

            {!!children && children}
          </div>
        </div>
      </article>
    )
  },
)

export default Dialog
